import type { ApiFilter, GridQueryInput } from './gridUtils';

type LocalSortValue = string | number | boolean | Date | null | undefined;
type LocalFilterValue = LocalSortValue | LocalSortValue[];
type ComparableValue =
  | { kind: 'number'; value: number }
  | { kind: 'string'; value: string }
  | { kind: 'boolean'; value: boolean }
  | { kind: 'null'; value: null };

const getSortValue = (row: unknown, field: string): LocalSortValue => {
  const record = row as Record<string, LocalSortValue>;
  return record[field];
};

const getRowFilterValue = (row: unknown, field: string): LocalFilterValue => {
  const record = row as Record<string, LocalFilterValue>;
  return record[field];
};

const normaliseSortValue = (value: LocalSortValue): string | number => {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return value.toLowerCase();
  return '';
};

const compareSortValues = (left: LocalSortValue, right: LocalSortValue): number => {
  if (left === null || left === undefined) {
    return right === null || right === undefined ? 0 : 1;
  }
  if (right === null || right === undefined) return -1;
  const leftValue = normaliseSortValue(left);
  const rightValue = normaliseSortValue(right);
  if (leftValue < rightValue) return -1;
  if (leftValue > rightValue) return 1;
  return 0;
};

const sortLocalRows = <T>(rows: T[], sorts: GridQueryInput['sorts']): T[] => {
  if (sorts.length === 0) return rows;
  const [{ field, direction }] = sorts;
  if (!field) return rows;
  const multiplier = direction === 'desc' ? -1 : 1;
  return [...rows].sort((left, right) => {
    const leftValue = getSortValue(left, field);
    const rightValue = getSortValue(right, field);
    const comparison = compareSortValues(leftValue, rightValue);
    if (isNullish(leftValue) || isNullish(rightValue)) {
      return comparison;
    }
    return comparison * multiplier;
  });
};

const toComparable = (value: LocalSortValue): ComparableValue => {
  if (value === null || value === undefined) return { kind: 'null', value: null };
  if (value instanceof Date) return { kind: 'number', value: value.getTime() };
  if (typeof value === 'number') return { kind: 'number', value };
  if (typeof value === 'boolean') return { kind: 'boolean', value };
  if (typeof value === 'string') return { kind: 'string', value: value.toLowerCase() };
  return { kind: 'string', value: String(value).toLowerCase() };
};

const isNullish = (value: unknown): value is null | undefined =>
  value === null || value === undefined;

const normaliseFilterValue = (value: ApiFilter['value']): LocalSortValue[] => {
  if (Array.isArray(value)) {
    return value as LocalSortValue[];
  }
  return [value as LocalSortValue];
};

const matchesStringOperator = (
  rowValue: LocalSortValue,
  filterValue: LocalSortValue,
  operator: 'contains' | 'startsWith' | 'endsWith',
): boolean => {
  if (isNullish(filterValue)) return false;
  const rowText = toComparable(rowValue).value;
  const filterText = toComparable(filterValue).value;
  if (typeof rowText !== 'string' || typeof filterText !== 'string') return false;
  if (operator === 'contains') return rowText.includes(filterText);
  if (operator === 'startsWith') return rowText.startsWith(filterText);
  return rowText.endsWith(filterText);
};

const compareComparable = (
  rowValue: LocalSortValue,
  filterValue: LocalSortValue,
): number | null => {
  if (isNullish(rowValue) || isNullish(filterValue)) return null;
  const rowComparable = toComparable(rowValue);
  const filterComparable = toComparable(filterValue);
  if (rowComparable.kind === 'number' && filterComparable.kind === 'number') {
    return rowComparable.value - filterComparable.value;
  }
  if (rowComparable.kind === 'boolean' && filterComparable.kind === 'boolean') {
    return Number(rowComparable.value) - Number(filterComparable.value);
  }
  const rowText = String(rowComparable.value ?? '');
  const filterText = String(filterComparable.value ?? '');
  return rowText.localeCompare(filterText);
};

const matchesFilter = (rowValue: LocalSortValue, filter: ApiFilter): boolean => {
  const [filterValue] = normaliseFilterValue(filter.value);
  switch (filter.operator) {
    case 'eq': {
      if (isNullish(filterValue)) return isNullish(rowValue);
      const rowComparable = toComparable(rowValue);
      const filterComparable = toComparable(filterValue);
      if (rowComparable.kind === filterComparable.kind) {
        return rowComparable.value === filterComparable.value;
      }
      return String(rowComparable.value ?? '') === String(filterComparable.value ?? '');
    }
    case 'neq': {
      if (isNullish(filterValue)) return !isNullish(rowValue);
      return !matchesFilter(rowValue, { ...filter, operator: 'eq' });
    }
    case 'contains':
      return matchesStringOperator(rowValue, filterValue, 'contains');
    case 'startsWith':
      return matchesStringOperator(rowValue, filterValue, 'startsWith');
    case 'endsWith':
      return matchesStringOperator(rowValue, filterValue, 'endsWith');
    case 'in': {
      const values = normaliseFilterValue(filter.value);
      return values.some((value) => matchesFilter(rowValue, { ...filter, operator: 'eq', value }));
    }
    case 'gt': {
      const comparison = compareComparable(rowValue, filterValue);
      return comparison !== null && comparison > 0;
    }
    case 'gte': {
      const comparison = compareComparable(rowValue, filterValue);
      return comparison !== null && comparison >= 0;
    }
    case 'lt': {
      const comparison = compareComparable(rowValue, filterValue);
      return comparison !== null && comparison < 0;
    }
    case 'lte': {
      const comparison = compareComparable(rowValue, filterValue);
      return comparison !== null && comparison <= 0;
    }
    default:
      return true;
  }
};

const matchesRowFilters = (row: unknown, filters: GridQueryInput['filters']): boolean => {
  if (filters.length === 0) return true;
  return filters.every((filter) => {
    const rowValue = getRowFilterValue(row, filter.field);
    if (Array.isArray(rowValue)) {
      return rowValue.some((value) => matchesFilter(value, filter));
    }
    return matchesFilter(rowValue, filter);
  });
};

const filterLocalRows = <T>(rows: T[], filters: GridQueryInput['filters']): T[] =>
  filters.length === 0 ? rows : rows.filter((row) => matchesRowFilters(row, filters));

export const buildLocalQueryResult = <T>(
  rows: T[],
  input: GridQueryInput,
): { items: T[]; total: number; page: number; pageSize: number } => {
  const filtered = filterLocalRows(rows, input.filters);
  const sorted = sortLocalRows(filtered, input.sorts);
  const start = Math.max(0, (input.page - 1) * input.pageSize);
  const items = sorted.slice(start, start + input.pageSize);
  return { items, total: filtered.length, page: input.page, pageSize: input.pageSize };
};
